import { FormikHelpers } from "formik";
import { TFunction } from "i18next";
import { Map } from "immutable";
import * as yup from "yup";
import { OpenRightPanel } from "../../../../types/common/rightPanel";
import { toastResponseError } from "../../../../utils/responseMessageHelper";

export interface InboundAddressFormValues {
  name: string;
  phone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
}

export const inboundAddressFormik = (
  t: TFunction,
  panelData: any,
  createInboundAddress: (data: { inbound_address: any }) => Promise<any>,
  updateInboundAddress: (
    id: number,
    data: { inbound_address: any },
  ) => Promise<any>,
  openRightPanel: OpenRightPanel,
  closeRightPanel: () => void,
) => {
  const address = panelData.get("address");
  const getValue = (name: string) =>
    (address ? address.get(name, "") : "") || "";

  const requiredString = (fieldKey: string) =>
    yup
      .string()
      .required(t("error.required", { field: t(fieldKey) }));

  const leaveForm = (preselectAddressId?: number) => {
    const returnPreviousPanel = panelData.get("returnPreviousPanel");
    if (!returnPreviousPanel) {
      closeRightPanel();
      return;
    }
    const initialPanelData = panelData.get("initialPanelData", Map());
    openRightPanel(
      returnPreviousPanel,
      preselectAddressId
        ? initialPanelData.set("preselectAddressId", preselectAddressId)
        : initialPanelData,
    );
  };

  return {
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: false,
    initialValues: {
      name: getValue("name") as string,
      phone: getValue("phone") as string,
      addressLine1: getValue("address_line_1") as string,
      addressLine2: getValue("address_line_2") as string,
      city: getValue("city") as string,
      state: getValue("state") as string,
      zipCode: getValue("zip_code") as string,
    },
    validationSchema: () =>
      yup.object().shape({
        name: requiredString("inbound.addresses.name"),
        phone: yup
          .string()
          .matches(/(\D*\d){5,}/, t("error.phoneNumberIsNotFully"))
          .required(t("error.required", { field: t("inbound.addresses.phone") })),
        addressLine1: requiredString("inbound.addresses.addressLine1"),
        addressLine2: yup.string(),
        city: requiredString("inbound.addresses.city"),
        state: requiredString("inbound.addresses.state"),
        zipCode: requiredString("inbound.addresses.zipCode"),
      }),
    onSubmit: (
      values: InboundAddressFormValues,
      { setSubmitting }: FormikHelpers<InboundAddressFormValues>,
    ) => {
      const payload = {
        inbound_address: {
          name: values.name,
          phone: values.phone.trim(),
          address_line_1: values.addressLine1,
          address_line_2: values.addressLine2 || null,
          city: values.city,
          state: values.state,
          zip_code: values.zipCode,
        },
      };

      if (panelData.get("isEditForm")) {
        return updateInboundAddress(panelData.get("addressId"), payload)
          .then(() => leaveForm())
          .catch((response: Error) => toastResponseError(response))
          .finally(() => setSubmitting(false));
      }

      return createInboundAddress(payload)
        .then((resp) => leaveForm(resp.value.getIn(["data", "id"])))
        .catch((response: Error) => toastResponseError(response))
        .finally(() => setSubmitting(false));
    },
  };
};
